
import { Question } from "../types/assessment";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Info, HelpCircle } from "lucide-react";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "./ui/tooltip";
import { useState } from "react";
import { detailedDimensionDescriptions } from "@/data/questions";
import { useIsMobile } from "@/hooks/use-mobile";

interface AssessmentQuestionProps {
  question: Question;
  onAnswer: (value: number) => void;
  currentAnswer?: number;
}

export const AssessmentQuestion = ({
  question,
  onAnswer,
  currentAnswer
}: AssessmentQuestionProps) => {
  console.log("Rendering question", question.id, "with current answer:", currentAnswer);
  // Track tooltip open state
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleValueChange = (value: string) => {
    console.log("Selected value for question", question.id, ":", value);
    onAnswer(parseInt(value));
  };

  // Get tooltip content based on the specific question
  const getTooltipContent = (question: Question) => {
    // Use question-specific tooltips from the question object if available
    if (question.tooltip) {
      return question.tooltip;
    }
    
    // Fallback to detailed dimension description
    return detailedDimensionDescriptions[question.dimension] || 
      "Select the option that best describes your organization's current state.";
  };

  // Toggle tooltip visibility
  const toggleTooltip = () => {
    setTooltipOpen(!tooltipOpen);
  };

  return (
    <Card className={`p-4 sm:p-6 shadow-lg ${isMobile ? 'rounded-lg' : 'rounded-xl'}`}>
      <div className="flex items-start gap-2 mb-3 sm:mb-4">
        <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold pr-2`}>{question.text}</h3>
        <TooltipProvider>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild onClick={toggleTooltip}>
              <button 
                className="inline-flex items-center justify-center rounded-full w-6 h-6 bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors flex-shrink-0 mt-0.5"
                aria-label="Question info"
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              className="max-w-xs p-3 bg-white shadow-lg rounded-md border text-sm"
              side={isMobile ? "bottom" : "right"}
              align={isMobile ? "center" : "start"}
            >
              <p className="font-medium text-gray-900 mb-1">About this question:</p>
              <p className="text-gray-700">{getTooltipContent(question)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <RadioGroup
        value={currentAnswer?.toString() || ""}
        onValueChange={handleValueChange}
        className="space-y-2 sm:space-y-3"
      >
        {question.options.map((option) => (
          <div key={option.value} className="flex items-start space-x-2 sm:space-x-3 p-2 rounded-md hover:bg-gray-50 transition-colors">
            <RadioGroupItem 
              value={option.value.toString()} 
              id={`${question.id}-${option.value}`}
              className="mt-1"
            />
            <div className="flex-1">
              <Label 
                htmlFor={`${question.id}-${option.value}`} 
                className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium`}
              >
                {option.label}
              </Label>
              {option.description && (
                <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 mt-1`}>{option.description}</p>
              )}
            </div>
          </div>
        ))}
      </RadioGroup>
    </Card>
  );
};
